import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStatusInfo } from '../utils/orderStatus';

export default function Orders() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get('type') || 'buyer';
  
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [dateRange, setDateRange] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [sellerType, setSellerType] = useState('all');
  const [retentionDays, setRetentionDays] = useState(365);
  const [showOld, setShowOld] = useState(true);

  // Set default tab based on order type
  useEffect(() => {
    if (orderType === 'vendor') {
      if (user?.role === 'producer' || user?.role === 'hub') {
        setActiveTab('dispatch');
      } else {
        setActiveTab('my');
      }
    } else {
      setActiveTab('my');
    }
  }, [orderType, user]);

  const getPaymentMethod = (order) => {
    const provider = order.payments?.[0]?.provider;
    switch(provider) {
      case 'stripe': return 'Credit Card';
      case 'cash': return 'Cash on Delivery';
      case 'bank_transfer': return 'Bank Transfer';
      case 'credits': return 'Pickup Credits';
      default: return order.paymentMethod || 'Unknown';
    }
  };

  useEffect(() => {
    if (activeTab === 'consignment' || activeTab === 'hub-sales') {
      fetchSales();
    } else {
      fetchOrders();
    }
  }, [activeTab, dateRange, statusFilter, customerSearch, sellerType, retentionDays, showOld]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      if (activeTab === 'my') endpoint = '/api/orders/my-orders';
      else if (activeTab === 'dispatch') {
        if (user?.role === 'hub') endpoint = '/api/orders/hub-orders';
        else if (user?.role === 'producer') endpoint = '/api/orders/received-orders';
      }
      if (!endpoint) { setOrders([]); setLoading(false); return; }
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      let ordersData = Array.isArray(data) ? data : (data.orders || []);
      if (activeTab !== 'my') {
        if (dateRange !== 'all') {
          const days = parseInt(dateRange);
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - days);
          ordersData = ordersData.filter(o => new Date(o.createdAt) >= cutoff);
        }
        if (statusFilter === 'ready') {
          ordersData = ordersData.filter(o => o.readyForPickup === true && o.status === 'pending');
        } else if (statusFilter !== 'all') {
          ordersData = ordersData.filter(o => o.status === statusFilter);
        }
        if (customerSearch.trim()) {
          const term = customerSearch.toLowerCase();
          ordersData = ordersData.filter(o => (o.buyer?.name && o.buyer.name.toLowerCase().includes(term)) || (o.buyer?.email && o.buyer.email.toLowerCase().includes(term)));
        }
      }
      setOrders(ordersData);
    } catch (error) { console.error(error); setOrders([]); } finally { setLoading(false); }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'consignment' ? '/api/hub/consignment-sales' : '/api/farm/hub-sales';
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setSales(Array.isArray(data) ? data : []);
    } catch (error) { console.error(error); setSales([]); } finally { setLoading(false); }
  };

  const toggleReadyForPickup = async (orderId, currentStatus) => {
    try {
      const res = await fetch(`/api/orders/${orderId}/ready`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.readyForPickup ? 'Order marked ready for pickup' : 'Order no longer ready for pickup');
        fetchOrders();
      }
    } catch (error) { console.error(error); }
  };

  const bulkSetReady = async (ready) => {
    if (selectedOrders.length === 0) return;
    setProcessing(true);
    try {
      await fetch('/api/orders/bulk-ready', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ orderIds: selectedOrders, ready })
      });
      fetchOrders();
      setSelectedOrders([]);
    } catch (error) { console.error(error); } finally { setProcessing(false); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedOrders(orders.map(o => o._id));
    else setSelectedOrders([]);
  };

  const handleSelectOne = (orderId) => {
    setSelectedOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const getStatusColor = (status) => {
    const info = getStatusInfo(status);
    switch(info.label) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Claimed (Waiting for Pickup)': return 'bg-blue-100 text-blue-800';
      case 'In Transit': return 'bg-purple-100 text-purple-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Refunded': return 'bg-gray-100 text-gray-800';
      case 'Damaged': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  const statusFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'ready', label: 'Ready for Pickup' },
    { value: 'claimed', label: 'Claimed' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const buyerStatusFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'claimed', label: 'Claimed' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' }
  ];

  if (loading) return <div className="text-center py-8">Loading...</div>;

  const isVendorView = orderType === 'vendor';
  const isProducer = user?.role === 'producer';
  const isHub = user?.role === 'hub';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">
          {isVendorView ? '📋 Sell Orders' : '📋 My Orders'}
        </h1>
        <span className="text-sm text-gray-500">
          {isVendorView ? 'Orders from your customers' : 'Orders you have placed'}
        </span>
      </div>

      <div className="border-b mb-6">
        <nav className="flex flex-wrap gap-4">
          {!isVendorView && (
            <button 
              onClick={() => setActiveTab('my')} 
              className={`pb-2 px-1 ${activeTab === 'my' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
            >
              My Orders
            </button>
          )}
          {isVendorView && (isProducer || isHub) && (
            <button 
              onClick={() => setActiveTab('dispatch')} 
              className={`pb-2 px-1 ${activeTab === 'dispatch' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
            >
              🚚 Dispatch Manager
            </button>
          )}
          {isVendorView && isHub && (
            <button 
              onClick={() => setActiveTab('consignment')} 
              className={`pb-2 px-1 ${activeTab === 'consignment' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
            >
              📊 Consignment Sales
            </button>
          )}
          {isVendorView && isProducer && (
            <button 
              onClick={() => setActiveTab('hub-sales')} 
              className={`pb-2 px-1 ${activeTab === 'hub-sales' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500'}`}
            >
              📊 Hub Sales
            </button>
          )}
        </nav>
      </div>

      {/* Dispatch Manager */}
      {activeTab === 'dispatch' && (
        <div>
          <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date Range</label>
              <select 
                value={dateRange} 
                onChange={(e) => setDateRange(e.target.value)} 
                className="mt-1 block w-full border rounded-md px-3 py-2"
              >
                <option value="all">All time</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Order Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)} 
                className="mt-1 block w-full border rounded-md px-3 py-2"
              >
                {statusFilterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Customer</label>
              <input 
                type="text" 
                value={customerSearch} 
                onChange={(e) => setCustomerSearch(e.target.value)} 
                placeholder="Search by name or email" 
                className="mt-1 block w-full border rounded-md px-3 py-2" 
              />
            </div>
            <button 
              onClick={() => { setDateRange('all'); setStatusFilter('all'); setCustomerSearch(''); }} 
              className="bg-gray-200 px-4 py-2 rounded-md text-sm"
            >
              Clear
            </button>
          </div>

          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <button 
                onClick={() => bulkSetReady(true)} 
                disabled={selectedOrders.length === 0 || processing} 
                className="bg-green-600 text-white px-3 py-1 rounded disabled:bg-gray-400"
              >
                Bulk Mark Ready
              </button>
              <button 
                onClick={() => bulkSetReady(false)} 
                disabled={selectedOrders.length === 0 || processing} 
                className="bg-red-600 text-white px-3 py-1 rounded disabled:bg-gray-400"
              >
                Bulk Revoke Ready
              </button>
            </div>
            <div className="text-sm text-gray-500">{selectedOrders.length} selected</div>
          </div>

          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No orders match filters.</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input 
                        type="checkbox" 
                        onChange={handleSelectAll} 
                        checked={selectedOrders.length === orders.length && orders.length > 0} 
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Order #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Customer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Total</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Fulfillment</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Pickup</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Payment</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map(order => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input 
                          type="checkbox" 
                          checked={selectedOrders.includes(order._id)} 
                          onChange={() => handleSelectOne(order._id)} 
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">#{order._id.slice(-6).toUpperCase()}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(order.createdAt)}</td>
                      <td className="px-4 py-3 text-sm">{order.buyer?.name || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm font-medium">${order.totalAmount.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                          {getStatusInfo(order.status).label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {order.status === 'delivered' ? (
                          <span className="text-green-600">✓ Delivered</span>
                        ) : order.status === 'cancelled' ? (
                          <span className="text-red-600">✗ Cancelled</span>
                        ) : order.readyForPickup ? (
                          <span className="text-green-600">✓ Ready for pickup</span>
                        ) : order.status === 'in_transit' ? (
                          <span className="text-blue-600">🚚 In Transit</span>
                        ) : order.status === 'claimed' ? (
                          <span className="text-purple-600">📦 Claimed</span>
                        ) : (
                          <span className="text-yellow-600">⏳ Preparing</span>
                        )}
                        {order.readyForPickupAt && order.status !== 'delivered' && (
                          <div className="text-xs text-gray-400">
                            Ready: {new Date(order.readyForPickupAt).toLocaleTimeString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{order.assignedPickupId?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm">{getPaymentMethod(order)}</td>
                      <td className="px-4 py-3">
                        {order.status === 'pending' && (
                          <button 
                            onClick={() => toggleReadyForPickup(order._id, order.readyForPickup)} 
                            className={`px-2 py-1 rounded text-xs ${order.readyForPickup ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}`}
                          >
                            {order.readyForPickup ? 'Revoke Ready' : 'Mark Ready'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/order-confirmation/${order._id}`} className="text-blue-600 hover:underline text-sm">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Consignment Sales */}
      {activeTab === 'consignment' && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Consignment Sales (by farmer)</h2>
          {sales.length === 0 ? <p className="text-gray-500">No consignment sales yet.</p> : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Order #</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Date</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Product</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Farmer</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Buyer</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Sale Price</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Commission</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Farmer Earnings</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">View</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s, idx) => (
                    <tr key={idx}>
                      <td className="border px-4 py-2 font-mono text-sm">#{s.orderId.toString().slice(-8).toUpperCase()}</td>
                      <td className="border px-4 py-2 text-sm">{new Date(s.orderDate).toLocaleDateString()}</td>
                      <td className="border px-4 py-2 text-sm">{s.productName} x{s.quantity}</td>
                      <td className="border px-4 py-2 text-sm">{s.farmerName}</td>
                      <td className="border px-4 py-2 text-sm">{s.buyerName}</td>
                      <td className="border px-4 py-2 text-right text-sm">${s.salePrice.toFixed(2)}</td>
                      <td className="border px-4 py-2 text-right text-sm">${s.commissionAmount.toFixed(2)} ({s.commissionRate}%)</td>
                      <td className="border px-4 py-2 text-right text-sm font-medium">${s.farmerEarnings.toFixed(2)}</td>
                      <td className="border px-4 py-2 text-center">
                        <Link to={`/order-confirmation/${s.orderId}`} className="text-blue-600 hover:underline text-sm" target="_blank">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Hub Sales */}
      {activeTab === 'hub-sales' && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Sales through Hubs</h2>
          {sales.length === 0 ? <p className="text-gray-500">No hub sales yet.</p> : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Order #</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Date</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Product</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Hub</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">Buyer</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Sale Price</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Commission</th>
                    <th className="border px-4 py-2 text-right text-sm font-medium text-gray-500">Your Earnings</th>
                    <th className="border px-4 py-2 text-left text-sm font-medium text-gray-500">View</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s, idx) => (
                    <tr key={idx}>
                      <td className="border px-4 py-2 font-mono text-sm">#{s.orderId.toString().slice(-8).toUpperCase()}</td>
                      <td className="border px-4 py-2 text-sm">{new Date(s.orderDate).toLocaleDateString()}</td>
                      <td className="border px-4 py-2 text-sm">{s.productName} x{s.quantity}</td>
                      <td className="border px-4 py-2 text-sm">{s.hubName}</td>
                      <td className="border px-4 py-2 text-sm">{s.buyerName}</td>
                      <td className="border px-4 py-2 text-right text-sm">${s.salePrice.toFixed(2)}</td>
                      <td className="border px-4 py-2 text-right text-sm">${s.commissionAmount.toFixed(2)} ({s.commissionRate}%)</td>
                      <td className="border px-4 py-2 text-right text-sm font-medium">${s.yourEarnings.toFixed(2)}</td>
                      <td className="border px-4 py-2 text-center">
                        <Link to={`/order-confirmation/${s.orderId}`} className="text-blue-600 hover:underline text-sm" target="_blank">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* My Orders - with product thumbnails */}
      {activeTab === 'my' && (
        <div>
          <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date Range</label>
              <select 
                value={dateRange} 
                onChange={(e) => setDateRange(e.target.value)} 
                className="mt-1 block w-full border rounded-md px-3 py-2"
              >
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Seller Type</label>
              <select 
                value={sellerType} 
                onChange={(e) => setSellerType(e.target.value)} 
                className="mt-1 block w-full border rounded-md px-3 py-2"
              >
                <option value="all">All sellers</option>
                <option value="hub">Hubs only</option>
                <option value="producer">Producers only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Order Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)} 
                className="mt-1 block w-full border rounded-md px-3 py-2"
              >
                {buyerStatusFilterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Keep orders for</label>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="number" 
                  min="1" 
                  max="365" 
                  value={retentionDays} 
                  onChange={(e) => setRetentionDays(parseInt(e.target.value))} 
                  className="w-20 border rounded-md px-2 py-2" 
                />
                <span className="text-sm">days</span>
              </div>
            </div>
            <button 
              onClick={() => setShowOld(!showOld)} 
              className="bg-gray-200 px-4 py-2 rounded-md text-sm"
            >
              {showOld ? "Hide old orders" : "Show orders older than retention"}
            </button>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No orders match your filters.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => {
                const statusInfo = getStatusInfo(order.status);
                const productItems = order.products || order.items || [];
                
                return (
                  <div key={order._id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Order #{order._id.slice(-8).toUpperCase()}</p>
                        <p className="text-sm text-gray-500">Placed on {formatDate(order.createdAt)}</p>
                        <p className="text-sm text-gray-500">
                          Seller: {order.hubId?.name || order.producer?.farmName || order.producer?.name || 'Unknown'}
                        </p>
                        {order.metadata?.isKinOrder && (
                          <p className="text-sm text-purple-600 font-medium">
                           🤝 Kin Order
                          </p>
                        )}
                        <p className="text-sm text-gray-500">Pickup: {order.assignedPickupId?.name || 'Not assigned'}</p>
                        <p className="text-sm text-gray-500">Payment: {getPaymentMethod(order)}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    
                    <div className="border-t border-b py-4 my-4">
                      {productItems.map((item, idx) => {
                        // Try multiple ways to get the image
                        let imageUrl = null;
                        if (item.product?.images?.[0]) {
                          imageUrl = `/uploads/${item.product.images[0]}`;
                        } else if (item.productId?.images?.[0]) {
                          imageUrl = `/uploads/${item.productId.images[0]}`;
                        } else if (item.image) {
                          imageUrl = item.image.startsWith('http') ? item.image : `/uploads/${item.image}`;
                        } else if (item.thumbnail) {
                          imageUrl = item.thumbnail.startsWith('http') ? item.thumbnail : `/uploads/${item.thumbnail}`;
                        }
                        
                        return (
                          <div key={idx} className="flex items-center gap-4 py-2">
                            <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                              {imageUrl ? (
                                <img 
                                  src={imageUrl} 
                                  alt={item.name} 
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    parent.innerHTML = `
                                      <div class="w-full h-full flex items-center justify-center bg-green-100 text-green-700 text-lg font-bold">
                                        ${item.name ? item.name.charAt(0).toUpperCase() : '📦'}
                                      </div>
                                    `;
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-700 text-lg font-bold">
                                  {item.name ? item.name.charAt(0).toUpperCase() : '📦'}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 flex justify-between">
                              <span>{item.name} x {item.quantity}</span>
                              <span>${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <p className="font-bold">Total: ${order.totalAmount.toFixed(2)}</p>
                      <Link to={`/order-confirmation/${order._id}`} className="text-green-600 hover:underline">
                        Details
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
